import React, { useState, useEffect, useRef } from 'react';
import { X, Search, MessageCircle, Book, Settings, Users, FileText, Calendar, Package, Target, Send, Loader, Mail, Zap, DollarSign, FolderOpen, UserPlus, Link } from 'lucide-react';
import { generateTextWithChatGpt } from '../../lib/services/chatGptService';
import { useSettingsStore } from '../../lib/stores/settingsStore';

interface GuideSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  searchTerms: string[];
}

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export function GuideModal({ isOpen, onClose }: GuideModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const { settings } = useSettingsStore();
  const chatMessagesRef = useRef<HTMLDivElement>(null);

  // Function to check if a menu item is visible
  const isMenuItemVisible = (menuItem: string) => {
    const menuSettings = settings?.menu_settings;
    const hiddenItems = menuSettings?.hidden_menu_items || [];
    const disabledItems = menuSettings?.disabled_menu_items || [];
    return !hiddenItems.includes(menuItem) && !disabledItems.includes(menuItem);
  };

  // Function to navigate to a section
  const navigateToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    setShowChat(false);
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, isLoadingChat]);

  const sections: GuideSection[] = [
    {
      id: 'overview',
      title: 'Panoramica',
      icon: <Book className="h-5 w-5" />,
      searchTerms: ['panoramica', 'generale', 'introduzione', 'overview'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Benvenuto nella Guida CRM</h3>
            <p className="text-gray-600 mb-4">
              Questa guida ti accompagnerà nell'utilizzo completo del CRM, dalle impostazioni iniziali alla gestione avanzata di clienti, progetti e automazioni.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">🚀 Inizia qui</h4>
              <p className="text-sm text-blue-700">Configura le impostazioni base per iniziare</p>
              <button
                onClick={() => navigateToSection('settings')}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
              >
                → Vai alle Impostazioni
              </button>
            </div>
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">💬 Assistente IA</h4>
              <p className="text-sm text-green-700">Chiedi all'IA per supporto immediato</p>
              <button
                onClick={() => setShowChat(true)}
                className="mt-2 text-xs text-green-600 hover:text-green-800 underline"
              >
                → Apri Chat IA
              </button>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-3">Funzionalità Principali del CRM:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">👥 Gestione Clienti e Lead</p>
                <p className="text-xs text-gray-600 mt-1">Pipeline vendita, conversioni, anagrafica completa</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">📄 Offerte e Preventivi</p>
                <p className="text-xs text-gray-600 mt-1">Template personalizzabili, generazione PDF, tracciamento</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">📊 Gestione Progetti</p>
                <p className="text-xs text-gray-600 mt-1">Task, milestone, Gantt, tracking ore e costi</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">💬 WhatsApp Integration</p>
                <p className="text-xs text-gray-600 mt-1">Chat diretta, automazioni AI, template messaggi</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">📧 Newsletter e Email</p>
                <p className="text-xs text-gray-600 mt-1">Campagne email, segmentazione, analytics</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">⚡ Workflow Automation</p>
                <p className="text-xs text-gray-600 mt-1">Automazioni personalizzate, trigger, azioni</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">💰 Fatturazione Elettronica</p>
                <p className="text-xs text-gray-600 mt-1">Invoice Queue, Fatture in Cloud, matching automatico</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">📦 Magazzino</p>
                <p className="text-xs text-gray-600 mt-1">Inventario, movimenti, sync WooCommerce</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">📅 Calendar & Task</p>
                <p className="text-xs text-gray-600 mt-1">Pianificazione, reminder, sincronizzazione Google</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">🤝 Gestione Partner</p>
                <p className="text-xs text-gray-600 mt-1">Partner esterni, task assignment, commissioni</p>
              </div>
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-sm">🔗 Integrazioni</p>
                <p className="text-xs text-gray-600 mt-1">Google, Fatture in Cloud, WooCommerce, API custom</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-3">Percorso consigliato per iniziare:</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>
                Configura le{' '}
                <button
                  onClick={() => navigateToSection('settings')}
                  className="text-blue-600 hover:text-blue-800 underline font-semibold"
                >
                  Impostazioni
                </button>{' '}
                di base (dati azienda, utenti, servizi)
              </li>
              <li>Imposta le <strong>Categorie</strong> e i <strong>Servizi</strong> che offri</li>
              <li>
                Crea i tuoi primi{' '}
                {isMenuItemVisible('leads') && (
                  <button
                    onClick={() => navigateToSection('leads')}
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    Lead
                  </button>
                )}{' '}
                e convertili in{' '}
                {isMenuItemVisible('clients') && (
                  <button
                    onClick={() => navigateToSection('clients')}
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    Clienti
                  </button>
                )}
              </li>
              <li>
                Genera le prime{' '}
                {isMenuItemVisible('quotes') && (
                  <button
                    onClick={() => navigateToSection('offers')}
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    Offerte
                  </button>
                )}{' '}
                con template personalizzati
              </li>
              <li>
                Gestisci{' '}
                {isMenuItemVisible('projects') && (
                  <button
                    onClick={() => navigateToSection('projects')}
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                  >
                    Progetti
                  </button>
                )}{' '}
                con task e milestone
              </li>
              <li>
                Esplora le funzionalità avanzate: <strong>WhatsApp</strong>, <strong>Newsletter</strong>, <strong>Workflow</strong>, <strong>Fatturazione</strong>
              </li>
              <li>
                Configura le{' '}
                <button
                  onClick={() => navigateToSection('integrations')}
                  className="text-blue-600 hover:text-blue-800 underline font-semibold"
                >
                  Integrazioni
                </button>{' '}
                con i tuoi strumenti preferiti
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
            <h4 className="font-semibold text-yellow-800 mb-2">💡 Suggerimento</h4>
            <p className="text-sm text-yellow-700">
              Usa l'<strong>Assistente IA</strong> in qualsiasi momento per ottenere aiuto immediato su funzionalità specifiche,
              troubleshooting o best practices. L'IA conosce tutte le funzionalità del CRM e può guidarti passo-passo!
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'settings',
      title: 'Impostazioni',
      icon: <Settings className="h-5 w-5" />,
      searchTerms: ['impostazioni', 'configurazione', 'settings', 'setup', 'azienda', 'organizzazione'],
      content: (
        <div className="space-y-8">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-blue-800">⚙️ Configurazione Impostazioni - Guida Completa</h3>
            <p className="text-gray-700 mb-6 text-lg">
              Le impostazioni sono il fondamento del tuo CRM. Questa guida ti accompagnerà passo-passo nella configurazione iniziale e in tutte le personalizzazioni avanzate.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
              <p className="text-sm text-blue-800">
                <strong>📍 Come accedere:</strong> Clicca sull'icona ingranaggio nella barra laterale sinistra o vai nel menu principale → Impostazioni
              </p>
            </div>
          </div>

          <div className="space-y-8">
            {/* STEP 1: Dati Azienda */}
            <div className="border-2 border-blue-200 rounded-xl p-6 bg-blue-50">
              <h4 className="text-xl font-bold mb-4 flex items-center text-blue-800">
                <div className="w-8 h-8 bg-[#0f6cbd] text-white rounded-full flex items-center justify-center text-sm mr-3">1</div>
                🏢 Dati Azienda - PRIORITÀ MASSIMA
              </h4>
              <div className="ml-11 space-y-4">
                <p className="text-sm text-gray-700 font-medium">Prima configurazione da completare obbligatoriamente:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">📝 Informazioni Base</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• <strong>Nome Azienda:</strong> Ragione sociale completa</li>
                      <li>• <strong>Indirizzo:</strong> Via, CAP, Città, Provincia</li>
                      <li>• <strong>P.IVA/Codice Fiscale:</strong> Dati fiscali</li>
                      <li>• <strong>Telefono/Email:</strong> Contatti principali</li>
                      <li>• <strong>Sito Web:</strong> URL aziendale (opzionale)</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">🖼️ Logo Aziendale</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• <strong>Formato:</strong> PNG, JPG (max 2MB)</li>
                      <li>• <strong>Dimensioni:</strong> 300x100px consigliate</li>
                      <li>• <strong>Utilizzo:</strong> Automatico in PDF offerte</li>
                      <li>• <strong>Posizione:</strong> Header di tutti i documenti</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <p className="text-sm text-green-800">
                    <strong>✅ Risultato:</strong> Questi dati appariranno automaticamente in tutte le offerte PDF, fatture e comunicazioni ufficiali
                  </p>
                </div>
              </div>
            </div>

            {/* STEP 2: Gestione Utenti */}
            <div className="border-2 border-purple-200 rounded-xl p-6 bg-purple-50">
              <h4 className="text-xl font-bold mb-4 flex items-center text-purple-800">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm mr-3">2</div>
                👥 Gestione Utenti e Permessi
              </h4>
              <div className="ml-11 space-y-4">
                <p className="text-sm text-gray-700">Configura il team che utilizzerà il CRM con ruoli e permessi specifici:</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2 text-red-600">🔴 Admin</h5>
                    <ul className="text-xs space-y-1 text-red-600">
                      <li>• Accesso completo</li>
                      <li>• Gestione impostazioni</li>
                      <li>• Creazione utenti</li>
                      <li>• Modifiche globali</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2 text-blue-600">🔵 Manager</h5>
                    <ul className="text-xs space-y-1 text-blue-600">
                      <li>• Gestione clienti</li>
                      <li>• Creazione offerte</li>
                      <li>• Report e analytics</li>
                      <li>• Gestione progetti</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2 text-green-600">🟢 User</h5>
                    <ul className="text-xs space-y-1 text-green-600">
                      <li>• Visualizza clienti</li>
                      <li>• Task assegnati</li>
                      <li>• Lead personali</li>
                      <li>• Calendar interno</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                  <p className="text-sm text-orange-800">
                    <strong>⚠️ Procedura:</strong> 1) Vai in Impostazioni → Utenti 2) Clicca "Nuovo Utente" 3) Inserisci email 4) Seleziona ruolo 5) Invia invito
                  </p>
                </div>
              </div>
            </div>

            {/* STEP 3: Categorie */}
            <div className="border-2 border-green-200 rounded-xl p-6 bg-green-50">
              <h4 className="text-xl font-bold mb-4 flex items-center text-green-800">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm mr-3">3</div>
                📂 Categorie Prodotti/Servizi - FONDAMENTALE
              </h4>
              <div className="ml-11 space-y-4">
                <p className="text-sm text-gray-700">Prima di creare servizi, devi organizzarli in categorie logiche:</p>
                
                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-semibold text-sm mb-3">💡 Esempi di Categorie</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="font-medium mb-1">🏢 Agenzia Web:</p>
                      <ul className="space-y-1 text-gray-600 ml-2">
                        <li>• Sviluppo Siti Web</li>
                        <li>• Marketing Digitale</li>
                        <li>• Consulenza IT</li>
                        <li>• Manutenzione</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-1">⚖️ Studio Legale:</p>
                      <ul className="space-y-1 text-gray-600 ml-2">
                        <li>• Diritto Civile</li>
                        <li>• Diritto Penale</li>
                        <li>• Consulenza Aziendale</li>
                        <li>• Pratiche Immobiliari</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                  <p className="text-sm text-red-800">
                    <strong>🚨 ORDINE CRITICO:</strong> 1° Crea Categorie → 2° Crea Servizi. I servizi senza categoria non possono essere creati!
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-semibold text-sm mb-2">📋 Procedura Dettagliata</h5>
                  <ol className="text-xs space-y-1 text-gray-700 list-decimal list-inside">
                    <li>Vai in <strong>Impostazioni → Categorie</strong></li>
                    <li>Clicca il pulsante <strong>"Nuova Categoria"</strong></li>
                    <li>Inserisci <strong>Nome</strong> (es: "Sviluppo Web")</li>
                    <li>Aggiungi <strong>Descrizione</strong> dettagliata</li>
                    <li>Scegli un <strong>Colore</strong> identificativo</li>
                    <li>Clicca <strong>"Salva"</strong></li>
                    <li>Ripeti per tutte le categorie necessarie</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* STEP 4: Template Servizi */}
            <div className="border-2 border-yellow-200 rounded-xl p-6 bg-yellow-50">
              <h4 className="text-xl font-bold mb-4 flex items-center text-yellow-800">
                <div className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm mr-3">4</div>
                🎯 Template Servizi con Variabili - AUTOMAZIONE
              </h4>
              <div className="ml-11 space-y-4">
                <p className="text-sm text-gray-700">Crea descrizioni automatiche che si adattano a ogni cliente:</p>
                
                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-semibold text-sm mb-3">🔄 Variabili Disponibili</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div>
                      <p className="font-medium mb-2">👤 Cliente:</p>
                      <ul className="space-y-1 text-gray-600">
                        <li><code className="bg-gray-100 px-1 rounded">{'{{client_name}}'}</code> → Nome cliente</li>
                        <li><code className="bg-gray-100 px-1 rounded">{'{{client_email}}'}</code> → Email cliente</li>
                        <li><code className="bg-gray-100 px-1 rounded">{'{{client_phone}}'}</code> → Telefono</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-medium mb-2">💰 Servizio:</p>
                      <ul className="space-y-1 text-gray-600">
                        <li><code className="bg-gray-100 px-1 rounded">{'{{service_title}}'}</code> → Titolo servizio</li>
                        <li><code className="bg-gray-100 px-1 rounded">{'{{service_amount}}'}</code> → Prezzo</li>
                        <li><code className="bg-gray-100 px-1 rounded">{'{{total_amount}}'}</code> → Totale offerta</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="font-semibold text-sm mb-2">📝 Esempio Pratico</h5>
                  <div className="bg-gray-50 p-3 rounded text-xs font-mono">
                    <p className="mb-2"><strong>Template:</strong></p>
                    <p className="text-gray-700 mb-3">
                      "Gentile {'{{client_name}}'}, proponiamo il servizio {'{{service_title}}'} per un investimento di {'{{service_amount}}'} €. 
                      Questo servizio include sviluppo completo e assistenza 12 mesi."
                    </p>
                    <p className="mb-2"><strong>Risultato automatico:</strong></p>
                    <p className="text-green-700">
                      "Gentile Mario Rossi, proponiamo il servizio Sito Web E-commerce per un investimento di 2.500 €. 
                      Questo servizio include sviluppo completo e assistenza 12 mesi."
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* STEP 5: Configurazioni Avanzate */}
            <div className="border-2 border-indigo-200 rounded-xl p-6 bg-indigo-50">
              <h4 className="text-xl font-bold mb-4 flex items-center text-indigo-800">
                <div className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm mr-3">5</div>
                🚀 Configurazioni Avanzate
              </h4>
              <div className="ml-11 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">💳 Metodi di Pagamento</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• Configurazione IBAN aziendale</li>
                      <li>• Integrazione PayPal Business</li>
                      <li>• Setup carte di credito</li>
                      <li>• Rate personalizzate</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">📊 Commissioni Smart</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• Riduzione automatica per clienti fedeli</li>
                      <li>• Percentuali per categoria servizio</li>
                      <li>• Soglie di fatturato</li>
                      <li>• Bonus team commerciale</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">📱 WhatsApp Business</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• Messaggi automatici clienti</li>
                      <li>• Notifiche scadenze</li>
                      <li>• Follow-up lead</li>
                      <li>• Template personalizzati</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="font-semibold text-sm mb-2">🔄 Automazioni</h5>
                    <ul className="text-xs space-y-1 text-gray-600">
                      <li>• Task ricorrenti automatiche</li>
                      <li>• Email follow-up</li>
                      <li>• Promemoria scadenze</li>
                      <li>• Report automatici</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Riepilogo finale */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl border-2 border-blue-300">
              <h4 className="text-xl font-bold mb-4 text-blue-800">✅ Checklist Configurazione Completa</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-semibold mb-2">🏁 Setup Base (Obbligatorio):</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li>□ Dati azienda completi + logo</li>
                    <li>□ Prima categoria servizi creata</li>
                    <li>□ Primo servizio di prova</li>
                    <li>□ Utente team aggiunto</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold mb-2">⭐ Ottimizzazioni (Consigliate):</h5>
                  <ul className="space-y-1 text-gray-700">
                    <li>□ Template variabili configurati</li>
                    <li>□ Metodi pagamento impostati</li>
                    <li>□ WhatsApp integrato</li>
                    <li>□ Automazioni attivate</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 bg-green-100 p-3 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  🎉 Completata la configurazione? Sei pronto per{' '}
                  {isMenuItemVisible('leads') && (
                    <button
                      onClick={() => navigateToSection('leads')}
                      className="text-green-700 hover:text-green-900 underline font-semibold"
                    >
                      creare i primi Lead
                    </button>
                  )}{' '}
                  {isMenuItemVisible('leads') && isMenuItemVisible('clients') && 'e '}{' '}
                  {isMenuItemVisible('clients') && (
                    <button
                      onClick={() => navigateToSection('clients')}
                      className="text-green-700 hover:text-green-900 underline font-semibold"
                    >
                      gestire i Clienti
                    </button>
                  )}!
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'leads',
      title: 'Lead',
      icon: <Target className="h-5 w-5" />,
      searchTerms: ['lead', 'prospects', 'potenziali clienti', 'acquisizione'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Gestione Lead</h3>
            <p className="text-gray-600 mb-6">
              I lead sono potenziali clienti da convertire. Gestiscili efficacemente per massimizzare le conversioni.
            </p>
          </div>

          <div className="space-y-6">
            <div className="border border-green-200 rounded-lg p-4 bg-green-50">
              <h4 className="font-semibold mb-3 text-green-800">📈 Flusso Lead Ottimale</h4>
              <div className="flex items-center space-x-2 text-sm">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">Nuovo</span>
                <span>→</span>
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">Contattato</span>
                <span>→</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">Qualificato</span>
                <span>→</span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded">Cliente</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">➕ Creazione Lead</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• Nome e contatti completi</li>
                  <li>• Fonte di acquisizione</li>
                  <li>• Interesse specifico</li>
                  <li>• Budget stimato</li>
                  <li>• Note iniziali</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">🔄 Gestione Stati</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Nuovo:</strong> Lead appena acquisito</li>
                  <li>• <strong>Contattato:</strong> Primo contatto effettuato</li>
                  <li>• <strong>Qualificato:</strong> Lead con potenziale</li>
                  <li>• <strong>Perso:</strong> Lead non convertito</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-2 text-blue-800">💡 Best Practices</h4>
              <ul className="text-sm space-y-1 text-blue-700">
                <li>• Contatta i nuovi lead entro 24 ore</li>
                <li>• Usa le note per tracciare ogni interazione</li>
                <li>• Imposta promemoria per i follow-up</li>
                <li>• Qualifica i lead con domande specifiche</li>
                <li>
                  • Converti i lead qualificati in{' '}
                  {isMenuItemVisible('clients') && (
                    <button
                      onClick={() => navigateToSection('clients')}
                      className="text-blue-800 hover:text-blue-900 underline font-semibold"
                    >
                      clienti
                    </button>
                  )}
                </li>
              </ul>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-600">
                  💡 <strong>Tip:</strong> Una volta convertito un lead in cliente, puoi{' '}
                  {isMenuItemVisible('quotes') && (
                    <button
                      onClick={() => navigateToSection('offers')}
                      className="text-blue-700 hover:text-blue-900 underline"
                    >
                      creare offerte personalizzate
                    </button>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'clients',
      title: 'Clienti',
      icon: <Users className="h-5 w-5" />,
      searchTerms: ['clienti', 'customers', 'anagrafica', 'contatti'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Gestione Clienti</h3>
            <p className="text-gray-600 mb-6">
              L'anagrafica clienti è fondamentale per tenere traccia di tutti i contatti e la storicità dei rapporti.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">👤</div>
                <h4 className="font-semibold text-blue-800 mt-2">Privati</h4>
                <p className="text-xs text-blue-600 mt-1">Clienti privati e consumatori</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-600">🏢</div>
                <h4 className="font-semibold text-green-800 mt-2">Aziende</h4>
                <p className="text-xs text-green-600 mt-1">Clienti business e corporate</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-2xl font-bold text-purple-600">🤝</div>
                <h4 className="font-semibold text-purple-800 mt-2">Partner</h4>
                <p className="text-xs text-purple-600 mt-1">Collaboratori e fornitori</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3">📋 Dati Cliente Completi</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">Informazioni Base</h5>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Nome/Ragione sociale</li>
                    <li>• Codice fiscale/P.IVA</li>
                    <li>• Indirizzo completo</li>
                    <li>• Telefono e email</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Dati Commerciali</h5>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Categoria di appartenenza</li>
                    <li>• Referente principale</li>
                    <li>• Metodo pagamento preferito</li>
                    <li>• Note e preferenze</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <h4 className="font-semibold mb-2 text-yellow-800">⚡ Funzioni Avanzate</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <ul className="space-y-1 text-yellow-700">
                  <li>• Storico progetti e servizi</li>
                  <li>• Fatturato totale e trend</li>
                  <li>• Promemoria e scadenze</li>
                </ul>
                <ul className="space-y-1 text-yellow-700">
                  <li>• Integrazione WhatsApp</li>
                  <li>• Export dati in PDF/Excel</li>
                  <li>• Gestione documenti allegati</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'offers',
      title: 'Offerte',
      icon: <FileText className="h-5 w-5" />,
      searchTerms: ['offerte', 'preventivi', 'quote', 'proposte', 'pdf'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Creazione Offerte</h3>
            <p className="text-gray-600 mb-6">
              Le offerte sono il cuore del processo commerciale. Crea preventivi professionali e convincenti.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border">
              <h4 className="font-semibold mb-3">🎯 Processo Offerta Ottimale</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm">
                <div className="text-center">
                  <div className="w-8 h-8 bg-[#0f6cbd] text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">1</div>
                  <p className="font-medium">Analisi Esigenze</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">2</div>
                  <p className="font-medium">Selezione Servizi</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">3</div>
                  <p className="font-medium">Personalizzazione</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">4</div>
                  <p className="font-medium">Invio Cliente</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">📝 Elementi Chiave</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Introduzione:</strong> Presenta il valore</li>
                  <li>• <strong>Servizi:</strong> Dettaglio con template</li>
                  <li>• <strong>Prezzi:</strong> Trasparenti e competitivi</li>
                  <li>• <strong>Condizioni:</strong> Pagamento e consegna</li>
                  <li>• <strong>Validità:</strong> Scadenza dell'offerta</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">🎨 Template Variabili</h4>
                <div className="space-y-2 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <code>{'{{client_name}}'}</code> → Nome cliente
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <code>{'{{service_amount}}'}</code> → Importo servizio
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <code>{'{{total_amount}}'}</code> → Totale offerta
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <code>{'{{service_title}}'}</code> → Titolo servizio
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold mb-2 text-green-800">✨ Funzioni Avanzate</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <ul className="space-y-1 text-green-700">
                  <li>• Generazione PDF automatica</li>
                  <li>• IA per descrizioni servizi</li>
                  <li>• Duplicazione offerte esistenti</li>
                </ul>
                <ul className="space-y-1 text-green-700">
                  <li>• Tracking stato offerte</li>
                  <li>• Promemoria follow-up</li>
                  <li>• Conversione in progetti</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'projects',
      title: 'Progetti',
      icon: <Package className="h-5 w-5" />,
      searchTerms: ['progetti', 'projects', 'gestione', 'task', 'attività'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Gestione Progetti</h3>
            <p className="text-gray-600 mb-6">
              I progetti ti permettono di organizzare il lavoro, tracciare i progressi e gestire le attività del team.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border">
              <h4 className="font-semibold mb-3">🚀 Lifecycle Progetto</h4>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
                <div className="text-center">
                  <div className="w-6 h-6 bg-gray-400 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">•</div>
                  <p>Pianificazione</p>
                </div>
                <div className="text-center">
                  <div className="w-6 h-6 bg-[#0f6cbd] text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">•</div>
                  <p>In Corso</p>
                </div>
                <div className="text-center">
                  <div className="w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">•</div>
                  <p>In Pausa</p>
                </div>
                <div className="text-center">
                  <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">•</div>
                  <p>Completato</p>
                </div>
                <div className="text-center">
                  <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-1 text-xs">•</div>
                  <p>Annullato</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">📋 Componenti Progetto</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Task:</strong> Attività specifiche</li>
                  <li>• <strong>Milestone:</strong> Obiettivi intermedi</li>
                  <li>• <strong>Timeline:</strong> Pianificazione temporale</li>
                  <li>• <strong>Risorse:</strong> Team e strumenti</li>
                  <li>• <strong>Budget:</strong> Controllo costi</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold mb-3">👥 Gestione Team</h4>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• Assegnazione responsabilità</li>
                  <li>• Tracking ore lavorate</li>
                  <li>• Comunicazione interna</li>
                  <li>• Report progresso</li>
                  <li>• Valutazione performance</li>
                </ul>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h4 className="font-semibold mb-2 text-purple-800">⚡ Automazioni Disponibili</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <ul className="space-y-1 text-purple-700">
                  <li>• Task ricorrenti automatiche</li>
                  <li>• Notifiche scadenze</li>
                  <li>• Report automatici</li>
                </ul>
                <ul className="space-y-1 text-purple-700">
                  <li>• Fatturazione milestone</li>
                  <li>• Aggiornamenti cliente</li>
                  <li>• Archiviazione documenti</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'calendar',
      title: 'Calendar',
      icon: <Calendar className="h-5 w-5" />,
      searchTerms: ['calendar', 'calendario', 'appuntamenti', 'eventi', 'programmazione'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">Gestione Calendar</h3>
            <p className="text-gray-600 mb-6">
              Il calendario integrato ti permette di organizzare appuntamenti, task e scadenze in un'unica vista.
            </p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Calendar className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <h4 className="font-semibold text-blue-800">Appuntamenti</h4>
                <p className="text-xs text-blue-600 mt-1">Riunioni e incontri clienti</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-2xl font-bold text-green-600">✓</div>
                <h4 className="font-semibold text-green-800">Task</h4>
                <p className="text-xs text-green-600 mt-1">Attività e scadenze</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-2xl font-bold text-purple-600">🔔</div>
                <h4 className="font-semibold text-purple-800">Promemoria</h4>
                <p className="text-xs text-purple-600 mt-1">Notifiche automatiche</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3">📅 Viste Calendar</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">Visualizzazioni</h5>
                  <ul className="space-y-1 text-gray-600">
                    <li>• <strong>Giorno:</strong> Dettaglio orario</li>
                    <li>• <strong>Settimana:</strong> Vista settimanale</li>
                    <li>• <strong>Mese:</strong> Panoramica mensile</li>
                    <li>• <strong>Lista:</strong> Elenco cronologico</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">Filtri Disponibili</h5>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Per tipo evento</li>
                    <li>• Per cliente/progetto</li>
                    <li>• Per responsabile</li>
                    <li>• Per priorità</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold mb-2 text-orange-800">🔄 Sincronizzazione</h4>
              <div className="text-sm text-orange-700 space-y-2">
                <p>Il calendario si sincronizza automaticamente con:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Task dei progetti</li>
                  <li>Scadenze offerte</li>
                  <li>Rinnovi servizi</li>
                  <li>Follow-up lead</li>
                  <li>Promemoria clienti</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold mb-2 text-green-800">💡 Tips Produttività</h4>
              <ul className="text-sm space-y-1 text-green-700">
                <li>• Imposta promemoria 15-30 min prima</li>
                <li>• Usa colori diversi per categorizzare</li>
                <li>• Blocca tempo per attività ricorrenti</li>
                <li>• Sincronizza con calendar esterni</li>
                <li>• Pianifica buffer tra appuntamenti</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'inventory',
      title: 'Magazzino',
      icon: <Package className="h-5 w-5" />,
      searchTerms: ['magazzino', 'inventory', 'prodotti', 'stock', 'giacenze', 'woocommerce', 'ecommerce', 'sincronizzazione', 'sync', 'zone', 'carico', 'scarico', 'stoccaggio', 'listini', 'prezzi', 'sku', 'barcode', 'noleggio'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-semibold mb-4">📦 Sistema Magazzino Avanzato</h3>
            <p className="text-gray-600 mb-6">
              Il modulo magazzino offre una gestione completa di prodotti, scorte, zone fisiche, movimenti e sincronizzazione e-commerce.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
              <p className="text-sm text-blue-800">
                <strong>📍 Accesso:</strong> Menu principale → Magazzino
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🚀 Setup Iniziale</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📁 1. Categorie Prodotti</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Categorie</p>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Crea categorie logiche (es: Elettronica, Abbigliamento)</li>
                  <li>• Sottocategorie con gerarchia parent-child</li>
                  <li>• Assegna colori per identificazione rapida</li>
                </ul>
                <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700">
                  <strong>⚠️</strong> Le categorie devono essere create PRIMA dei prodotti
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">💰 2. Listini Prezzi</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Listini</p>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• <strong>Standard:</strong> Prezzo base retail</li>
                  <li>• <strong>Grossisti:</strong> Prezzi scontati rivenditori</li>
                  <li>• <strong>Noleggio:</strong> Tariffe giornaliere/mensili</li>
                  <li>• <strong>Scaglioni:</strong> Prezzi automatici per volumi</li>
                </ul>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium mb-2">💡 Esempio Scaglioni Quantità</h5>
              <p className="text-sm text-gray-600 mb-2">Prodotto: Maglietta Logo Aziendale</p>
              <ul className="text-sm space-y-1 text-gray-600 ml-4">
                <li>• 1-10 pezzi: €15,00 cad.</li>
                <li>• 11-50 pezzi: €12,00 cad. (-20%)</li>
                <li>• 51-100 pezzi: €10,00 cad. (-33%)</li>
                <li>• 100+ pezzi: €8,00 cad. (-47%)</li>
              </ul>
            </div>

            <h4 className="text-lg font-semibold">🏷️ Gestione Prodotti</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">➕ Creazione Prodotti</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Prodotti → Nuovo</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Dati Obbligatori:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Nome prodotto chiaro</li>
                      <li>• SKU univoco (es: TSHIRT-BLU-M)</li>
                      <li>• Categoria esistente</li>
                      <li>• Stock iniziale</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Dati Opzionali:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Descrizione dettagliata</li>
                      <li>• Peso e dimensioni</li>
                      <li>• Stock minimo riordini</li>
                      <li>• Immagini multiple</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📋 Scheda Prodotto</h5>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Informazioni Base:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Nome, descrizione, SKU</li>
                      <li>• Categoria e stato</li>
                      <li>• Dimensioni fisiche</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Gestione Stock:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Quantità attuale</li>
                      <li>• Prenotazioni attive</li>
                      <li>• Storico movimenti</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Prezzi e Listini:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Prezzo per ogni listino</li>
                      <li>• Scaglioni automatici</li>
                      <li>• Saldi temporanei</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded border border-blue-200">
              <h5 className="font-medium text-blue-800 mb-1">🔍 Funzione Copia SKU</h5>
              <p className="text-sm text-blue-700">
                Nella lista prodotti, clicca l'icona accanto allo SKU per copiarlo negli appunti.
              </p>
            </div>

            <h4 className="text-lg font-semibold">🏗️ Zone Magazzino</h4>

            <p className="text-gray-600">Organizza fisicamente il magazzino per ottimizzare stoccaggio e picking:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🗺️ Configurazione Zone</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Zone Magazzino</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Codice:</strong> ID univoco (es: A-01)</li>
                  <li>• <strong>Nome:</strong> Descrizione (es: Scaffale A1)</li>
                  <li>• <strong>Ubicazione:</strong> Posizione fisica</li>
                  <li>• <strong>Capacità:</strong> Numero max prodotti</li>
                  <li>• <strong>Tipo:</strong> Standard/Refrigerato/Quarantena</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📊 Controllo Capacità</h5>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Controlli Automatici:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Occupazione vs capacità max</li>
                      <li>• Avvisi zona quasi piena (90%)</li>
                      <li>• Suggerimenti zone alternative</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Visualizzazione:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Progress bar occupazione</li>
                      <li>• Lista prodotti per zona</li>
                      <li>• Mappa distribuzione</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium mb-3">💡 Esempi Zone</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <p className="text-sm font-medium">🏪 Standard (STD-A1)</p>
                  <p className="text-xs text-gray-600">Scaffali prodotti finiti, temperatura normale, 100 articoli</p>
                </div>
                <div>
                  <p className="text-sm font-medium">❄️ Refrigerato (FRIGO-01)</p>
                  <p className="text-xs text-gray-600">Prodotti deperibili, 2-8°C, 50 articoli</p>
                </div>
                <div>
                  <p className="text-sm font-medium">🔄 Quarantena (QUAR-01)</p>
                  <p className="text-xs text-gray-600">Resi da controllare, accesso limitato, 25 articoli</p>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📦 Operazioni Quotidiane</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📥 Carico/Scarico</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Carico/Scarico</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Tipologie:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• <strong>Carico:</strong> Arrivo da fornitori</li>
                      <li>• <strong>Scarico:</strong> Uscita vendite</li>
                      <li>• <strong>Reso:</strong> Rientri clienti</li>
                      <li>• <strong>Regolarizzazione:</strong> Correzioni</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Dati Richiesti:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Prodotto (ricerca nome/SKU)</li>
                      <li>• Quantità da movimentare</li>
                      <li>• Motivo e note</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📍 Stoccaggio Zone</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Stoccaggio Zone</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Processo:</p>
                    <ol className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>1. Seleziona prodotto</li>
                      <li>2. Inserisci quantità</li>
                      <li>3. Scegli zona destinazione</li>
                      <li>4. Sistema verifica capacità</li>
                      <li>5. Auto-aggiorna occupazione</li>
                    </ol>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Modalità:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• <strong>Manuale:</strong> Scelta specifica</li>
                      <li>• <strong>Scan:</strong> Barcode zona</li>
                      <li>• <strong>Suggerita:</strong> Sistema propone</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-indigo-50 p-3 rounded border border-indigo-200">
              <h5 className="font-medium text-indigo-800 mb-1">💾 Salvataggio Automatico</h5>
              <p className="text-sm text-indigo-700 mb-1">
                Operazioni salvate temporaneamente nel browser per evitare perdite:
              </p>
              <ul className="text-sm text-indigo-600 space-y-1">
                <li>• Ripristino automatico se ricarichi per errore</li>
                <li>• Cancellazione automatica dopo salvataggio</li>
                <li>• Promemoria operazioni non salvate</li>
              </ul>
            </div>

            <h4 className="text-lg font-semibold">🏠 Sistema Noleggi</h4>

            <p className="text-gray-600">Gestione completa noleggi con controllo automatico disponibilità:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📋 Creazione Noleggio</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Magazzino → Noleggi → Nuovo</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• <strong>Cliente:</strong> Da anagrafica esistente</li>
                  <li>• <strong>Prodotti:</strong> Ricerca nome/SKU</li>
                  <li>• <strong>Periodo:</strong> Date inizio/fine</li>
                  <li>• <strong>Listino:</strong> Prezzi giornalieri/mensili</li>
                  <li>• <strong>Cauzione:</strong> Garanzia opzionale</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🔄 Stati Noleggio</h5>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">Ciclo di Vita:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• <strong>Prenotato:</strong> Stock prenotato</li>
                      <li>• <strong>Attivo:</strong> Consegnato al cliente</li>
                      <li>• <strong>Scaduto:</strong> Oltre data fine</li>
                      <li>• <strong>Completato:</strong> Rientrato</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Controlli:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Verifica disponibilità stock</li>
                      <li>• Controllo sovrapposizioni</li>
                      <li>• Calcolo automatico totali</li>
                      <li>• Notifiche scadenze</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-3 rounded border border-green-200">
              <h5 className="font-medium text-green-800 mb-1">📊 Dashboard Noleggi</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="font-medium">Panoramica:</p>
                  <ul className="text-green-700 ml-4 space-y-1">
                    <li>• Noleggi attivi tempo reale</li>
                    <li>• Prodotti attualmente noleggiati</li>
                    <li>• Fatturato mensile noleggi</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">Promemoria:</p>
                  <ul className="text-green-700 ml-4 space-y-1">
                    <li>• Scadenze imminenti (3 giorni)</li>
                    <li>• Noleggi scaduti</li>
                    <li>• Cauzioni da restituire</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">🛒 Integrazione WooCommerce</h4>

            <p className="text-gray-600">Sincronizzazione bidirezionale completa con listini multipli:</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">⚙️ Configurazione</h5>
                <p className="text-sm text-gray-600 mb-2"><strong>Percorso:</strong> Impostazioni → WooCommerce</p>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">API WooCommerce:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• URL sito (https://tuosito.com)</li>
                      <li>• Consumer Key/Secret da WooCommerce</li>
                      <li>• API Version (wc/v3 consigliata)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Webhook:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• API Key CRM generata automaticamente</li>
                      <li>• Endpoint ordini configurato</li>
                      <li>• Test connessione disponibile</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🔄 Sincronizzazione</h5>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">CRM → WooCommerce:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• Categorie con gerarchia</li>
                      <li>• Prodotti con SKU, immagini</li>
                      <li>• <strong>TUTTI</strong> i listini prezzi</li>
                      <li>• Scaglioni quantità</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">WooCommerce → CRM:</p>
                    <ul className="text-sm text-gray-600 ml-4 space-y-1">
                      <li>• <strong>Ordini automatici</strong></li>
                      <li>• <strong>Riduzione stock</strong> automatica</li>
                      <li>• Ripristino su cancellazioni</li>
                      <li>• Log completo operazioni</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
              <h5 className="font-medium text-yellow-800 mb-1">🆕 Plugin WordPress Richiesto</h5>
              <p className="text-sm text-yellow-700 mb-2">
                Installa "Advanced Price Lists for WooCommerce" per funzionalità complete:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="font-medium">Funzionalità:</p>
                  <ul className="text-yellow-700 ml-4 space-y-1">
                    <li>• Listini multipli</li>
                    <li>• Prezzi automatici quantità</li>
                    <li>• Utenti B2B dedicati</li>
                    <li>• Webhook ordini</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">Setup:</p>
                  <ul className="text-yellow-700 ml-4 space-y-1">
                    <li>• Inserisci API Key CRM</li>
                    <li>• Configura URL webhook</li>
                    <li>• Testa connessione</li>
                    <li>• Attiva sincronizzazione</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded border border-blue-200">
              <h5 className="font-medium text-blue-800 mb-1">📈 Monitoraggio</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="font-medium">Statistiche:</p>
                  <ul className="text-blue-700 ml-4 space-y-1">
                    <li>• Prodotti sincronizzati</li>
                    <li>• Ordini ricevuti 24h</li>
                    <li>• Errori sincronizzazione</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">Log:</p>
                  <ul className="text-blue-700 ml-4 space-y-1">
                    <li>• Webhook successi/errori</li>
                    <li>• Movimenti stock automatici</li>
                    <li>• Tracking ordini</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Vantaggi Sistema Completo</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">🎯 Controllo Totale</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Stock tempo reale</li>
                    <li>• Zone organizzate</li>
                    <li>• Movimenti tracciati</li>
                    <li>• Noleggi automatici</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">💰 Prezzi Avanzati</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Listini multipli</li>
                    <li>• Scaglioni automatici</li>
                    <li>• Saldi programmabili</li>
                    <li>• Sync WooCommerce</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">🚀 Automazione</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Ordini → Stock automatico</li>
                    <li>• Avvisi scorte minime</li>
                    <li>• Promemoria scadenze</li>
                    <li>• Backup locale</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-2 bg-green-100 rounded">
                <p className="text-sm text-green-800 font-medium">
                  🎉 Sistema integrato: clienti → offerte → progetti → magazzino → WooCommerce!
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp',
      icon: <MessageCircle className="h-5 w-5" />,
      searchTerms: ['whatsapp', 'chat', 'messaggi', 'conversazioni', 'wa', 'messaging', 'template', 'worker', 'ai', 'automazioni'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-green-800">💬 WhatsApp Business Integration</h3>
            <p className="text-gray-700 mb-6">
              Gestisci tutte le conversazioni WhatsApp direttamente dal CRM con automazioni AI, template e tracking completo.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500 mb-6">
              <p className="text-sm text-green-800">
                <strong>📍 Accesso:</strong> Menu principale → WhatsApp | Oppure dalla scheda Cliente/Lead
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🚀 Setup Iniziale</h4>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 className="font-semibold mb-3 text-blue-800">Configurazione WhatsApp</h5>
              <p className="text-sm text-gray-700 mb-3"><strong>Percorso:</strong> Menu principale → WhatsApp → Scansiona QR Code</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Configurazione iniziale:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Vai nella sezione WhatsApp del CRM</li>
                    <li>• Scansiona il QR code con il tuo WhatsApp Business</li>
                    <li>• Attendi la connessione (pochi secondi)</li>
                    <li>• Testa l'invio di un messaggio</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Verifica funzionamento:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>✅ Controlla stato connessione: "Attivo"</li>
                    <li>✅ Verifica ricezione messaggi in tempo reale</li>
                    <li>✅ Testa invio messaggio di prova</li>
                    <li>✅ Controlla sincronizzazione chat</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">💬 Funzionalità Chat</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📱 Conversazioni in Tempo Reale</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Inbox unificata:</strong> Tutte le chat in un'unica vista</li>
                  <li>• <strong>Associazione automatica:</strong> Messaggi collegati a clienti/lead esistenti</li>
                  <li>• <strong>Ricerca veloce:</strong> Cerca per nome, numero o contenuto</li>
                  <li>• <strong>Media support:</strong> Invia immagini, documenti, audio</li>
                  <li>• <strong>Template rapidi:</strong> Risposte predefinite personalizzabili</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🤖 Worker AI (Assistente Intelligente)</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Auto-risposta:</strong> Risposte immediate basate su AI</li>
                  <li>• <strong>Gestione lead:</strong> Crea/aggiorna lead automaticamente</li>
                  <li>• <strong>Comandi rapidi:</strong> "lista lead", "statistiche", "help"</li>
                  <li>• <strong>Follow-up intelligenti:</strong> Comprende il contesto della conversazione</li>
                  <li>• <strong>Fast routing:</strong> Pattern regex per risposte istantanee</li>
                </ul>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-semibold mb-3 text-purple-800">⚡ Comandi AI Worker Disponibili</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="font-medium mb-1">Lead Management:</p>
                  <ul className="text-purple-700 ml-4 space-y-1">
                    <li>• "lista lead"</li>
                    <li>• "i miei lead"</li>
                    <li>• "lead nuovi"</li>
                    <li>• "crea lead [nome]"</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-1">Clienti & Deal:</p>
                  <ul className="text-purple-700 ml-4 space-y-1">
                    <li>• "lista clienti"</li>
                    <li>• "i miei deal"</li>
                    <li>• "stats deal"</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-1">Utilità:</p>
                  <ul className="text-purple-700 ml-4 space-y-1">
                    <li>• "help" / "aiuto"</li>
                    <li>• "statistiche"</li>
                    <li>• "sì" / "no" / "annulla"</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📋 Template Messaggi</h4>

            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3"><strong>Percorso:</strong> Impostazioni → WhatsApp → Template Messaggi</p>
              <div className="space-y-3">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium text-sm mb-2">Come creare un template:</p>
                  <ol className="text-sm space-y-1 text-gray-600 ml-4 list-decimal">
                    <li>Vai in Impostazioni → WhatsApp → Template</li>
                    <li>Clicca "Nuovo Template"</li>
                    <li>Inserisci nome e messaggio (usa variabili come <code>{'{{client_name}}'}</code>)</li>
                    <li>Salva e usa con shortcut nella chat</li>
                  </ol>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium text-sm mb-2">Variabili disponibili:</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    <div>• <code>{'{{client_name}}'}</code></div>
                    <div>• <code>{'{{company_name}}'}</code></div>
                    <div>• <code>{'{{user_name}}'}</code></div>
                    <div>• <code>{'{{today}}'}</code></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">📱 Conversazioni:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Rispondi entro 24h per mantenere engagement</li>
                    <li>• Usa template per messaggi ricorrenti</li>
                    <li>• Tieni traccia di note importanti nella chat</li>
                    <li>• Associa sempre conversazioni a lead/clienti</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">🤖 AI Worker:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Addestra l'AI con comandi specifici aziendali</li>
                    <li>• Monitora le risposte AI per qualità</li>
                    <li>• Usa comandi fast-route per velocità</li>
                    <li>• Configura orari risposta automatica</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">⚠️ Nota Importante</h5>
              <p className="text-sm text-yellow-700">
                Per utilizzare WhatsApp è necessario un account WhatsApp Business attivo.
                Contatta il supporto per assistenza nella configurazione iniziale se necessario.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'newsletter',
      title: 'Newsletter',
      icon: <Mail className="h-5 w-5" />,
      searchTerms: ['newsletter', 'email', 'campagne', 'mailing', 'invio', 'email marketing', 'segmentazione', 'analytics'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-blue-800">📧 Sistema Newsletter e Campagne Email</h3>
            <p className="text-gray-700 mb-6">
              Crea e invia campagne email professionali ai tuoi clienti e lead con editor avanzato, segmentazione e analytics dettagliati.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-6">
              <p className="text-sm text-blue-800">
                <strong>📍 Accesso:</strong> Menu principale → Newsletter
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🚀 Creazione Campagna</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">1️⃣ Informazioni Base</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Nome campagna:</strong> Identificativo interno</li>
                  <li>• <strong>Oggetto email:</strong> Subject line accattivante</li>
                  <li>• <strong>Mittente:</strong> Nome e email mittente</li>
                  <li>• <strong>Tipo:</strong> Newsletter, Promozione, Aggiornamento</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">2️⃣ Segmentazione Destinatari</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Tutti i clienti:</strong> Invio massivo</li>
                  <li>• <strong>Per categoria:</strong> Es. solo aziende</li>
                  <li>• <strong>Per tag:</strong> Segmenti personalizzati</li>
                  <li>• <strong>Manuale:</strong> Selezione specifica</li>
                </ul>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium mb-3">✍️ Editor Email Avanzato</h5>
              <p className="text-sm text-gray-600 mb-3">L'editor supporta:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="font-medium mb-1">Formattazione:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Testo ricco (grassetto, corsivo, colori)</li>
                    <li>• Liste puntate e numerate</li>
                    <li>• Titoli e sottotitoli</li>
                    <li>• Immagini inline</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-1">Personalizzazione:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Variabili dinamiche (<code>{'{{nome}}'}</code>, <code>{'{{azienda}}'}</code>)</li>
                    <li>• Template salvati riutilizzabili</li>
                    <li>• Blocchi pre-costruiti</li>
                    <li>• Anteprima in tempo reale</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📊 Invio e Monitoraggio</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-blue-600">Modalità Invio</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Immediato:</strong> Invio istantaneo</li>
                  <li>• <strong>Programmato:</strong> Schedulazione data/ora</li>
                  <li>• <strong>Test:</strong> Invia a email di prova</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-green-600">Analytics</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <strong>Tasso apertura:</strong> % email aperte</li>
                  <li>• <strong>Click rate:</strong> % click su link</li>
                  <li>• <strong>Bounce:</strong> Email non consegnate</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-purple-600">Report</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• Dettaglio per destinatario</li>
                  <li>• Timeline aperture</li>
                  <li>• Esporta statistiche CSV</li>
                </ul>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-semibold mb-3 text-purple-800">🎨 Template Suggeriti</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium mb-1">📰 Newsletter Mensile</p>
                  <p className="text-gray-600">Aggiornamenti aziendali, news, case study</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium mb-1">🎁 Promozioni</p>
                  <p className="text-gray-600">Offerte speciali, sconti limitati</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium mb-1">📢 Annunci Prodotti</p>
                  <p className="text-gray-600">Nuovi servizi, feature, aggiornamenti</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-medium mb-1">🎉 Eventi</p>
                  <p className="text-gray-600">Webinar, workshop, inviti</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Email Marketing</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">📝 Contenuto:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Oggetto chiaro e accattivante (max 50 caratteri)</li>
                    <li>• Personalizza con nome destinatario</li>
                    <li>• Call-to-action visibile e chiara</li>
                    <li>• Mantieni design responsive (mobile-friendly)</li>
                    <li>• Testa sempre prima dell'invio massivo</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">⏰ Timing:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Martedì-Giovedì: migliori giorni per B2B</li>
                    <li>• Ore 10-11 o 14-15: picchi apertura</li>
                    <li>• Evita weekend per email professionali</li>
                    <li>• Non inviare più di 1-2 email/settimana</li>
                    <li>• Monitora analytics per ottimizzare</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">⚠️ Conformità GDPR</h5>
              <p className="text-sm text-yellow-700">
                Assicurati che tutti i destinatari abbiano dato il consenso esplicito (opt-in) per ricevere comunicazioni commerciali.
                Includi sempre il link di disiscrizione (unsubscribe) nelle tue email.
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'workflow',
      title: 'Workflow',
      icon: <Zap className="h-5 w-5" />,
      searchTerms: ['workflow', 'automazioni', 'automation', 'trigger', 'azioni', 'regole', 'flussi'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-orange-800">⚡ Workflow Automation</h3>
            <p className="text-gray-700 mb-6">
              Automatizza processi ripetitivi con workflow personalizzati basati su trigger e azioni. Risparmia tempo e migliora l'efficienza del team.
            </p>
            <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500 mb-6">
              <p className="text-sm text-orange-800">
                <strong>📍 Accesso:</strong> Impostazioni → Workflow Automation
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🎯 Concetti Base</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h5 className="font-semibold mb-2 text-blue-800">1. Trigger (Quando)</h5>
                <p className="text-sm text-gray-700">L'evento che avvia il workflow</p>
                <p className="text-xs text-gray-600 mt-2">Es: "Lead creato", "Offerta accettata"</p>
              </div>

              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h5 className="font-semibold mb-2 text-green-800">2. Condizioni (Se)</h5>
                <p className="text-sm text-gray-700">Filtri opzionali da verificare</p>
                <p className="text-xs text-gray-600 mt-2">Es: &quot;Budget &gt; €5000&quot;</p>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h5 className="font-semibold mb-2 text-purple-800">3. Azioni (Allora)</h5>
                <p className="text-sm text-gray-700">Cosa eseguire automaticamente</p>
                <p className="text-xs text-gray-600 mt-2">Es: "Invia email", "Crea task"</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold">🔔 Trigger Disponibili</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-blue-600">📋 Lead & Clienti</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Nuovo lead creato</li>
                  <li>• Lead cambia stato</li>
                  <li>• Lead convertito in cliente</li>
                  <li>• Cliente aggiornato</li>
                  <li>• Tag assegnato/rimosso</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-green-600">📄 Offerte & Deal</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Offerta creata</li>
                  <li>• Offerta accettata/rifiutata</li>
                  <li>• Deal cambia fase</li>
                  <li>• Deal vinto/perso</li>
                  <li>• Scadenza offerta imminente</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-purple-600">📊 Progetti & Task</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Progetto creato</li>
                  <li>• Progetto completato</li>
                  <li>• Task assegnata</li>
                  <li>• Task completata</li>
                  <li>• Milestone raggiunta</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-orange-600">⏰ Temporali</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Ogni giorno (schedulato)</li>
                  <li>• Ogni settimana</li>
                  <li>• Ogni mese</li>
                  <li>• Data specifica</li>
                  <li>• N giorni dopo evento</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">⚙️ Azioni Automatizzabili</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">📧 Comunicazioni</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Invia email automatica</li>
                  <li>• Invia messaggio WhatsApp</li>
                  <li>• Notifica interna team</li>
                  <li>• SMS (se configurato)</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">✅ Task & Attività</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Crea task assegnata</li>
                  <li>• Crea evento calendario</li>
                  <li>• Aggiorna campo record</li>
                  <li>• Assegna a utente</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🏷️ Gestione Dati</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Aggiungi/rimuovi tag</li>
                  <li>• Cambia stato/fase</li>
                  <li>• Duplica record</li>
                  <li>• Archivia record</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">🔗 Integrazioni</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Webhook HTTP personalizzato</li>
                  <li>• Crea documento Google Drive</li>
                  <li>• Invia a Zapier/Make</li>
                  <li>• API call custom</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">💡 Esempi Workflow Pratici</h4>

            <div className="space-y-3">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
                <p className="font-semibold text-blue-900 mb-2">🎯 Follow-up Automatico Lead</p>
                <div className="text-sm space-y-1 text-blue-800">
                  <p><strong>Trigger:</strong> Nuovo lead creato</p>
                  <p><strong>Azione 1:</strong> Invia email di benvenuto immediata</p>
                  <p><strong>Azione 2:</strong> Crea task "Chiamata follow-up" dopo 2 giorni</p>
                  <p><strong>Azione 3:</strong> Se non contattato dopo 7gg → Notifica manager</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500">
                <p className="font-semibold text-green-900 mb-2">✅ Onboarding Cliente</p>
                <div className="text-sm space-y-1 text-green-800">
                  <p><strong>Trigger:</strong> Offerta accettata</p>
                  <p><strong>Azione 1:</strong> Converti lead in cliente</p>
                  <p><strong>Azione 2:</strong> Crea progetto da template</p>
                  <p><strong>Azione 3:</strong> Invia email benvenuto con accessi</p>
                  <p><strong>Azione 4:</strong> Assegna project manager</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border-l-4 border-purple-500">
                <p className="font-semibold text-purple-900 mb-2">⏰ Rinnovo Automatico Servizi</p>
                <div className="text-sm space-y-1 text-purple-800">
                  <p><strong>Trigger:</strong> 30 giorni prima scadenza contratto</p>
                  <p><strong>Condizione:</strong> Cliente attivo</p>
                  <p><strong>Azione 1:</strong> Genera nuova offerta rinnovo</p>
                  <p><strong>Azione 2:</strong> Invia email con link pagamento</p>
                  <p><strong>Azione 3:</strong> Crea reminder per account manager</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Workflow</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">🎯 Design:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Inizia con workflow semplici (1 trigger → 1 azione)</li>
                    <li>• Testa sempre in ambiente di sviluppo</li>
                    <li>• Usa nomi descrittivi per workflow</li>
                    <li>• Documenta la logica con note interne</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">⚙️ Gestione:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Monitora log esecuzioni regolarmente</li>
                    <li>• Disattiva workflow non più necessari</li>
                    <li>• Evita loop infiniti (trigger circolari)</li>
                    <li>• Limita azioni email massicce</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">⚠️ Limiti e Considerazioni</h5>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Massimo 50 workflow attivi contemporaneamente</li>
                <li>• Azioni email: max 100/ora per workflow</li>
                <li>• Webhook: timeout 30 secondi</li>
                <li>• I workflow si eseguono in modo asincrono (non istantaneo)</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'invoicing',
      title: 'Fatturazione',
      icon: <FileText className="h-5 w-5" />,
      searchTerms: ['fatturazione', 'invoice', 'fatture', 'fattureincloud', 'matching', 'queue', 'elettronica'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-indigo-800">💰 Sistema Fatturazione Elettronica</h3>
            <p className="text-gray-700 mb-6">
              Gestione completa del ciclo di fatturazione con integrazione Fatture in Cloud, invoice queue automatica e matching intelligente.
            </p>
            <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500 mb-6">
              <p className="text-sm text-indigo-800">
                <strong>📍 Accesso:</strong> Menu principale → Invoice Queue / Invoice Matching
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🔗 Integrazione Fatture in Cloud</h4>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 className="font-semibold mb-3 text-blue-800">Setup Connessione</h5>
              <p className="text-sm text-gray-700 mb-3"><strong>Percorso:</strong> Impostazioni → Integrazioni → Fatture in Cloud</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Parametri richiesti:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• <strong>Company ID:</strong> ID azienda FiC</li>
                    <li>• <strong>API Key:</strong> Token autenticazione</li>
                    <li>• <strong>API Secret:</strong> Chiave segreta</li>
                    <li>• <strong>Environment:</strong> Production/Sandbox</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Test connessione:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>✅ Verifica credenziali API</li>
                    <li>✅ Sincronizza clienti esistenti</li>
                    <li>✅ Test creazione fattura bozza</li>
                    <li>✅ Controlla numerazione automatica</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📋 Invoice Queue (Coda Fatture)</h4>

            <div className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium mb-3">Cos'è l'Invoice Queue?</h5>
              <p className="text-sm text-gray-600 mb-3">
                Sistema automatico che genera fatture elettroniche da offerte accettate, progetti completati o abbonamenti ricorrenti.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Funzionalità:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Generazione automatica da offerte/progetti</li>
                    <li>• Fatturazione ricorrente (mensile/annuale)</li>
                    <li>• Schedulazione invio automatico</li>
                    <li>• Controllo duplicati intelligente</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Stati fattura:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• <span className="text-gray-500">Bozza:</span> In preparazione</li>
                    <li>• <span className="text-blue-500">In Coda:</span> Pronta per invio</li>
                    <li>• <span className="text-green-500">Inviata:</span> Consegnata FiC</li>
                    <li>• <span className="text-red-500">Errore:</span> Richiede intervento</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-semibold mb-3 text-purple-800">⚙️ Processo Automatico</h5>
              <ol className="text-sm space-y-2 text-purple-700 ml-4 list-decimal">
                <li><strong>Trigger:</strong> Offerta accettata / Progetto completato / Scadenza abbonamento</li>
                <li><strong>Creazione:</strong> Sistema genera fattura bozza con dati da CRM</li>
                <li><strong>Validazione:</strong> Controllo dati obbligatori (P.IVA, importi, codici fiscali)</li>
                <li><strong>Queue:</strong> Inserimento in coda con priorità</li>
                <li><strong>Invio FiC:</strong> Trasmissione automatica a Fatture in Cloud</li>
                <li><strong>Conferma:</strong> Notifica utente e aggiornamento stato</li>
              </ol>
            </div>

            <h4 className="text-lg font-semibold">🔍 Invoice Matching</h4>

            <div className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium mb-3">Riconciliazione Automatica</h5>
              <p className="text-sm text-gray-600 mb-3">
                Abbina automaticamente fatture Fatture in Cloud con clienti, offerte e pagamenti nel CRM.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-sm mb-2">Matching per:</p>
                  <ul className="text-sm space-y-1 text-gray-600 ml-4">
                    <li>• Codice cliente FiC ↔ Cliente CRM</li>
                    <li>• Numero fattura ↔ Offerta/Progetto</li>
                    <li>• P.IVA ↔ Anagrafica cliente</li>
                    <li>• Importo e data ↔ Transazione</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-sm mb-2">Azioni automatiche:</p>
                  <ul className="text-sm space-y-1 text-gray-600 ml-4">
                    <li>• Associa fattura a cliente/progetto</li>
                    <li>• Segna offerta come fatturata</li>
                    <li>• Aggiorna stato pagamento</li>
                    <li>• Genera nota credito se necessario</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h5 className="font-medium mb-3">📊 Dashboard Fatturazione</h5>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white p-3 rounded border text-center">
                  <p className="text-2xl font-bold text-blue-600">143</p>
                  <p className="text-gray-600">Fatture Mese</p>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                  <p className="text-2xl font-bold text-green-600">€45.2k</p>
                  <p className="text-gray-600">Fatturato</p>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                  <p className="text-2xl font-bold text-orange-600">12</p>
                  <p className="text-gray-600">In Coda</p>
                </div>
                <div className="bg-white p-3 rounded border text-center">
                  <p className="text-2xl font-bold text-purple-600">98%</p>
                  <p className="text-gray-600">Match Rate</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Fatturazione</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">📝 Dati Clienti:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Verifica sempre P.IVA/CF prima fatturazione</li>
                    <li>• Mantieni aggiornati indirizzi PEC</li>
                    <li>• Usa codice univoco SDI se disponibile</li>
                    <li>• Sincronizza anagrafica CRM ↔ FiC regolarmente</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">⚙️ Automazione:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Configura template fattura default</li>
                    <li>• Imposta numerazione automatica progressiva</li>
                    <li>• Abilita notifiche email errori queue</li>
                    <li>• Monitora matching rate settimanalmente</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">⚠️ Conformità Normativa</h5>
              <p className="text-sm text-yellow-700 mb-2">
                Le fatture elettroniche sono soggette a normativa fiscale italiana. Assicurati di:
              </p>
              <ul className="text-sm text-yellow-700 space-y-1 ml-4">
                <li>• Conservare digitalmente per 10 anni</li>
                <li>• Verificare marca da bollo quando obbligatoria (€77,47+)</li>
                <li>• Rispettare termini emissione (12 giorni operazione)</li>
                <li>• Gestire correttamente note credito e variazioni</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'commissions',
      title: 'Commissioni',
      icon: <DollarSign className="h-5 w-5" />,
      searchTerms: ['commissioni', 'commissions', 'provvigioni', 'percentuali', 'calcolo', 'vendite'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-green-800">💰 Sistema Commissioni</h3>
            <p className="text-gray-700 mb-6">
              Calcola automaticamente commissioni e provvigioni per il team vendita basate su deal chiusi, progetti completati o ricavi ricorrenti.
            </p>
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500 mb-6">
              <p className="text-sm text-green-800">
                <strong>📍 Accesso:</strong> Menu principale → Commissioni | Impostazioni → Commissioni
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">⚙️ Configurazione</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h5 className="font-semibold mb-3 text-blue-800">Percentuali Globali</h5>
                <p className="text-sm text-gray-700 mb-2"><strong>Percorso:</strong> Impostazioni → Commissioni</p>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• <strong>Standard:</strong> Percentuale default tutti utenti</li>
                  <li>• <strong>Manager:</strong> % maggiorata per responsabili</li>
                  <li>• <strong>Senior:</strong> % per venditori esperti</li>
                  <li>• <strong>Junior:</strong> % ridotta per nuovi</li>
                </ul>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h5 className="font-semibold mb-3 text-purple-800">Personalizzate per Utente</h5>
                <p className="text-sm text-gray-700 mb-2">Override individuale</p>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Assegna % specifica a ciascun venditore</li>
                  <li>• Scaglioni progressivi per obiettivi</li>
                  <li>• Bonus extra su servizi premium</li>
                  <li>• Penalità su churn clienti</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📊 Calcolo Commissioni</h4>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h5 className="font-medium mb-3">Base di Calcolo</h5>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="font-semibold mb-2 text-blue-600">Deal Vinti</p>
                  <p className="text-gray-600">Commissione su valore totale deal quando stato = "Vinto"</p>
                  <p className="text-xs text-gray-500 mt-2">Es: Deal €10k, commissione 5% = €500</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-semibold mb-2 text-green-600">Progetti Completati</p>
                  <p className="text-gray-600">Commissione su fatturato progetto a completamento</p>
                  <p className="text-xs text-gray-500 mt-2">Es: Progetto €15k, commissione 3% = €450</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="font-semibold mb-2 text-purple-600">Ricorrenze</p>
                  <p className="text-gray-600">Commissione mensile su MRR (Monthly Recurring Revenue)</p>
                  <p className="text-xs text-gray-500 mt-2">Es: MRR €2k/mese, commissione 10% = €200/mese</p>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <h5 className="font-medium mb-3">⚙️ Configurazione Avanzata</h5>
              <div className="space-y-3 text-sm">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium mb-1">Scaglioni Progressivi:</p>
                  <div className="ml-4 space-y-1 text-gray-600">
                    <p>• 0-50k fatturato/anno → 3%</p>
                    <p>• 50-100k fatturato/anno → 5%</p>
                    <p>• 100k+ fatturato/anno → 7%</p>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium mb-1">Riduzione Automatica:</p>
                  <p className="text-gray-600 ml-4">
                    Quando deal/progetto subisce sconto, commissione si riduce proporzionalmente
                  </p>
                  <p className="text-xs text-gray-500 ml-4 mt-1">
                    Es: Sconto 20% sul deal → Commissione ridotta del 20%
                  </p>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">📈 Dashboard Commissioni</h4>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500 text-center">
                <p className="text-3xl font-bold text-blue-700">€8.4k</p>
                <p className="text-sm text-blue-600">Questo Mese</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500 text-center">
                <p className="text-3xl font-bold text-green-700">€52k</p>
                <p className="text-sm text-green-600">Quest'Anno</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border-l-4 border-purple-500 text-center">
                <p className="text-3xl font-bold text-purple-700">18</p>
                <p className="text-sm text-purple-600">Deal Pagati</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg border-l-4 border-orange-500 text-center">
                <p className="text-3xl font-bold text-orange-700">4.8%</p>
                <p className="text-sm text-orange-600">% Media</p>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-semibold mb-3 text-purple-800">📋 Report Dettagliato</h5>
              <p className="text-sm text-gray-700 mb-3">Ogni venditore può visualizzare:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <ul className="space-y-1 text-purple-700">
                  <li>• Elenco deal/progetti con commissione</li>
                  <li>• Breakdown per mese/trimestre</li>
                  <li>• Confronto con target assegnato</li>
                  <li>• Trend storico performance</li>
                </ul>
                <ul className="space-y-1 text-purple-700">
                  <li>• Commissioni in attesa approvazione</li>
                  <li>• Commissioni pagate (storico)</li>
                  <li>• Prossimi pagamenti previsti</li>
                  <li>• Export PDF/Excel per contabilità</li>
                </ul>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Commissioni</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">🎯 Trasparenza:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Comunica chiaramente regole di calcolo al team</li>
                    <li>• Mostra breakdown dettagliato commissioni</li>
                    <li>• Pubblica target e obiettivi mensili</li>
                    <li>• Feedback regolare su performance</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">💡 Motivazione:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Bonus extra per superamento target</li>
                    <li>• Gare mensili/trimestrali con premi</li>
                    <li>• Percentuali crescenti su upsell clienti</li>
                    <li>• Riconoscimenti pubblici top performer</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">⚠️ Note Importanti</h5>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Le commissioni sono calcolate automaticamente ma richiedono approvazione manuale per pagamento</li>
                <li>• Commissioni su deal persi o progetti annullati vengono automaticamente annullate</li>
                <li>• Per MRR ricorrenti, commissioni si ripetono finché il servizio è attivo</li>
                <li>• Consulta sempre il commercialista per aspetti fiscali e contributivi</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'partners',
      title: 'Partner',
      icon: <UserPlus className="h-5 w-5" />,
      searchTerms: ['partner', 'collaboratori', 'esterni', 'fornitori', 'freelance', 'subappaltatori', 'task'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-teal-800">🤝 Gestione Partner Esterni</h3>
            <p className="text-gray-700 mb-6">
              Collabora con partner esterni, freelance e fornitori. Assegna task, monitora progresso e gestisci pagamenti.
            </p>
            <div className="bg-teal-50 p-4 rounded-lg border-l-4 border-teal-500 mb-6">
              <p className="text-sm text-teal-800">
                <strong>📍 Accesso:</strong> Menu principale → Partner | Partner Tasks
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">👥 Tipologie Partner</h4>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h5 className="font-semibold mb-2 text-blue-800">🎨 Creativi</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Graphic Designer</li>
                  <li>• Copywriter</li>
                  <li>• Video Maker</li>
                  <li>• Fotografi</li>
                </ul>
              </div>

              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h5 className="font-semibold mb-2 text-green-800">💻 Tecnici</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Sviluppatori</li>
                  <li>• Sistemisti</li>
                  <li>• Consulenti IT</li>
                  <li>• DevOps</li>
                </ul>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h5 className="font-semibold mb-2 text-purple-800">📊 Consulenti</h5>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Commercialisti</li>
                  <li>• Legali</li>
                  <li>• Marketing</li>
                  <li>• HR Specialist</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">➕ Creazione Partner</h4>

            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3"><strong>Percorso:</strong> Partner → Nuovo Partner</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Informazioni Base:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Nome e cognome / Ragione sociale</li>
                    <li>• Email e telefono</li>
                    <li>• P.IVA (se applicabile)</li>
                    <li>• Tipologia (Creativo, Tecnico, Consulente, etc.)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Dati Operativi:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Competenze specifiche (tag)</li>
                    <li>• Tariffa oraria / giornaliera</li>
                    <li>• Disponibilità (ore/settimana)</li>
                    <li>• Modalità pagamento preferita</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">✅ Partner Tasks</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">Assegnazione Task</h5>
                <ol className="text-sm space-y-2 text-gray-600 ml-4 list-decimal">
                  <li>Vai in Partner Tasks → Nuova Task</li>
                  <li>Seleziona partner dalla lista</li>
                  <li>Descrivi attività, scadenza, budget</li>
                  <li>Allega brief/materiali necessari</li>
                  <li>Invia notifica automatica al partner</li>
                </ol>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3">Stati Task Partner</h5>
                <ul className="text-sm space-y-2 text-gray-600">
                  <li>• <span className="text-gray-500">Da Assegnare:</span> Bozza interna</li>
                  <li>• <span className="text-blue-500">Assegnata:</span> Partner notificato</li>
                  <li>• <span className="text-orange-500">In Corso:</span> Lavoro iniziato</li>
                  <li>• <span className="text-purple-500">In Revisione:</span> Deliverable ricevuto</li>
                  <li>• <span className="text-green-500">Completata:</span> Approvata e chiusa</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h5 className="font-semibold mb-3 text-blue-800">📋 Dashboard Partner</h5>
              <p className="text-sm text-gray-700 mb-3">Vista dedicata per ogni partner mostra:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <ul className="space-y-1 text-blue-700">
                  <li>• Task assegnate (attive e completate)</li>
                  <li>• Ore lavorate totali</li>
                  <li>• Budget speso vs disponibile</li>
                  <li>• Performance rating (qualità/tempistiche)</li>
                </ul>
                <ul className="space-y-1 text-blue-700">
                  <li>• Progetti collaborati</li>
                  <li>• Pagamenti in sospeso</li>
                  <li>• Storico fatture/compensi</li>
                  <li>• Note e feedback interni</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">💰 Gestione Pagamenti</h4>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Tracking Costi:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• Budget task preventivato vs reale</li>
                    <li>• Timesheet ore lavorate (se oraria)</li>
                    <li>• Milestone pagamenti progressivi</li>
                    <li>• Ritenute fiscali automatiche (se applicabili)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Stati Pagamento:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>• <span className="text-orange-500">Da Pagare:</span> Task completata</li>
                    <li>• <span className="text-blue-500">In Elaborazione:</span> Bonifico in corso</li>
                    <li>• <span className="text-green-500">Pagato:</span> Ricevuta partner</li>
                    <li>• <span className="text-gray-500">Contestato:</span> Dispute aperta</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-semibold mb-3 text-purple-800">🎯 Workflow Tipico</h5>
              <div className="space-y-2 text-sm text-purple-700">
                <div className="flex items-start">
                  <span className="font-bold mr-2">1.</span>
                  <p><strong>Progetto Assegnato:</strong> Cliente richiede servizio che richiede competenze esterne</p>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-2">2.</span>
                  <p><strong>Selezione Partner:</strong> Cerchi partner con skill necessarie e disponibilità</p>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-2">3.</span>
                  <p><strong>Assegnazione Task:</strong> Crei task dettagliata con brief, scadenza, budget</p>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-2">4.</span>
                  <p><strong>Monitoraggio:</strong> Partner aggiorna stato, carica deliverable, traccia ore</p>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-2">5.</span>
                  <p><strong>Revisione:</strong> Approvi il lavoro o richiedi modifiche</p>
                </div>
                <div className="flex items-start">
                  <span className="font-bold mr-2">6.</span>
                  <p><strong>Pagamento:</strong> Task completata → Genera fattura → Paghi partner</p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Partner Management</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">🤝 Relazioni:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Mantieni database aggiornato competenze partner</li>
                    <li>• Valuta regolarmente performance (qualità/tempi)</li>
                    <li>• Comunica chiaramente aspettative e scadenze</li>
                    <li>• Costruisci pool partner affidabili per emergenze</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">💼 Amministrazione:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Richiedi sempre P.IVA e dati fiscali completi</li>
                    <li>• Definisci contratti quadro per collaborazioni ricorrenti</li>
                    <li>• Paga puntualmente per mantenere buone relazioni</li>
                    <li>• Tieni traccia NDA e accordi riservatezza</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'integrations',
      title: 'Integrazioni',
      icon: <Link className="h-5 w-5" />,
      searchTerms: ['integrazioni', 'integrations', 'api', 'connessioni', 'google', 'fattureincloud', 'woocommerce', 'zapier', 'webhook'],
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-2xl font-bold mb-4 text-indigo-800">🔗 Integrazioni e Connessioni</h3>
            <p className="text-gray-700 mb-6">
              Connetti il CRM ai tuoi strumenti preferiti per automatizzare flussi di lavoro e sincronizzare dati.
            </p>
            <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500 mb-6">
              <p className="text-sm text-indigo-800">
                <strong>📍 Accesso:</strong> Impostazioni → API e Integrazioni
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="text-lg font-semibold">🔌 Integrazioni Disponibili</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <h5 className="font-semibold mb-2 text-red-800 flex items-center">
                  <span className="text-2xl mr-2">📄</span> Fatture in Cloud
                </h5>
                <p className="text-sm text-gray-600 mb-2">Fatturazione elettronica italiana</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Sincronizzazione clienti</li>
                  <li>✓ Generazione fatture automatica</li>
                  <li>✓ Invio SDI</li>
                  <li>✓ Nota credito</li>
                </ul>
              </div>

              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h5 className="font-semibold mb-2 text-blue-800 flex items-center">
                  <span className="text-2xl mr-2">📧</span> Google Services
                </h5>
                <p className="text-sm text-gray-600 mb-2">Suite produttività Google</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Calendar sync bidirezionale</li>
                  <li>✓ Drive documenti</li>
                  <li>✓ Gmail invio email</li>
                  <li>✓ Meet videochiamate</li>
                </ul>
              </div>

              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h5 className="font-semibold mb-2 text-green-800 flex items-center">
                  <span className="text-2xl mr-2">💬</span> WhatsApp
                </h5>
                <p className="text-sm text-gray-600 mb-2">Messaggistica WhatsApp Business</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Chat in tempo reale</li>
                  <li>✓ Automazioni intelligenti</li>
                  <li>✓ Template messaggi</li>
                  <li>✓ Media support</li>
                </ul>
              </div>

              <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                <h5 className="font-semibold mb-2 text-purple-800 flex items-center">
                  <span className="text-2xl mr-2">🛒</span> WooCommerce
                </h5>
                <p className="text-sm text-gray-600 mb-2">E-commerce WordPress</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Sync prodotti bidirezionale</li>
                  <li>✓ Ordini → Stock automatico</li>
                  <li>✓ Categorie gerarchiche</li>
                  <li>✓ Listini prezzi multipli</li>
                </ul>
              </div>

              <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                <h5 className="font-semibold mb-2 text-orange-800 flex items-center">
                  <span className="text-2xl mr-2">⚡</span> Zapier / Make
                </h5>
                <p className="text-sm text-gray-600 mb-2">Automazioni no-code</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Webhook trigger eventi CRM</li>
                  <li>✓ Azioni personalizzate</li>
                  <li>✓ 3000+ app connesse</li>
                  <li>✓ Workflow complessi</li>
                </ul>
              </div>

              <div className="border-2 border-teal-200 rounded-lg p-4 bg-teal-50">
                <h5 className="font-semibold mb-2 text-teal-800 flex items-center">
                  <span className="text-2xl mr-2">🔧</span> API REST Custom
                </h5>
                <p className="text-sm text-gray-600 mb-2">Integrazioni sviluppatori</p>
                <ul className="text-xs space-y-1 text-gray-600">
                  <li>✓ Endpoints documentati</li>
                  <li>✓ OAuth 2.0</li>
                  <li>✓ Rate limiting</li>
                  <li>✓ Webhook custom</li>
                </ul>
              </div>
            </div>

            <h4 className="text-lg font-semibold">⚙️ Configurazione Integrazioni</h4>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-blue-700">📄 Fatture in Cloud - Setup</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-2">Passaggi:</p>
                    <ol className="space-y-1 text-gray-600 ml-4 list-decimal">
                      <li>Crea account su fattureincloud.it</li>
                      <li>Vai in FiC → Impostazioni → API</li>
                      <li>Genera API Key e Secret</li>
                      <li>Inserisci credenziali in CRM → Integrazioni</li>
                      <li>Sincronizza clienti esistenti</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Funzionalità abilitate:</p>
                    <ul className="space-y-1 text-gray-600 ml-4">
                      <li>• Invoice Queue automatica</li>
                      <li>• Matching fatture</li>
                      <li>• Sincronizzazione anagrafica</li>
                      <li>• Numerazione progressiva</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-blue-700">📧 Google Services - Setup</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-2">Passaggi:</p>
                    <ol className="space-y-1 text-gray-600 ml-4 list-decimal">
                      <li>Vai in CRM → Integrazioni → Google</li>
                      <li>Clicca "Connetti con Google"</li>
                      <li>Autorizza accessi richiesti (Calendar, Drive, Gmail)</li>
                      <li>Seleziona calendario primario</li>
                      <li>Configura cartella Drive backup</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium mb-2">Permessi richiesti:</p>
                    <ul className="space-y-1 text-gray-600 ml-4">
                      <li>• <strong>Calendar:</strong> Lettura/scrittura eventi</li>
                      <li>• <strong>Drive:</strong> Gestione file CRM folder</li>
                      <li>• <strong>Gmail:</strong> Invio email (solo send)</li>
                      <li>• <strong>Meet:</strong> Creazione link videocall</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium mb-3 text-green-700">💬 WhatsApp - Setup</h5>
                <p className="text-sm text-gray-600 mb-3">
                  Integra WhatsApp Business direttamente nel CRM per gestire tutte le conversazioni.
                </p>
                <div className="bg-gray-50 p-3 rounded text-sm">
                  <p className="font-medium mb-2">Configurazione:</p>
                  <ul className="space-y-1 text-gray-600 ml-4">
                    <li>1. Vai in Menu principale → WhatsApp</li>
                    <li>2. Clicca su "Connetti WhatsApp Business"</li>
                    <li>3. Scansiona il QR code con il tuo smartphone</li>
                    <li>4. Attendi la connessione (automatica)</li>
                    <li>5. Inizia a ricevere e inviare messaggi dal CRM</li>
                  </ul>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold">🔐 API REST CRM</h4>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-700 mb-3">
                Accedi programmaticamente ai dati CRM per creare integrazioni custom.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium mb-2">Endpoints Disponibili:</p>
                  <ul className="space-y-1 text-gray-600 ml-4 font-mono text-xs">
                    <li>• GET /api/clients</li>
                    <li>• POST /api/leads</li>
                    <li>• GET /api/deals/:id</li>
                    <li>• PUT /api/projects/:id</li>
                    <li>• DELETE /api/tasks/:id</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Autenticazione:</p>
                  <div className="bg-white p-2 rounded border font-mono text-xs">
                    <p className="text-gray-500">// Header richiesto</p>
                    <p>Authorization: Bearer YOUR_API_KEY</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Genera API Key in: Impostazioni → API → Nuova Chiave
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 mb-3">✨ Best Practices Integrazioni</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h5 className="font-medium mb-2">🔒 Sicurezza:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Non condividere mai API Key pubblicamente</li>
                    <li>• Ruota chiavi API ogni 3-6 mesi</li>
                    <li>• Usa HTTPS per tutte le chiamate API</li>
                    <li>• Limita permessi OAuth al minimo necessario</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium mb-2">⚙️ Manutenzione:</h5>
                  <ul className="space-y-1 text-green-700">
                    <li>• Monitora log errori integrazioni settimanalmente</li>
                    <li>• Testa connessioni dopo aggiornamenti CRM</li>
                    <li>• Mantieni credenziali aggiornate (scadenze token)</li>
                    <li>• Documenta integrazioni custom per il team</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h5 className="font-semibold text-yellow-800 mb-2">💡 Serve Aiuto?</h5>
              <p className="text-sm text-yellow-700">
                Per configurazioni complesse o integrazioni custom, contatta il supporto tecnico per assistenza personalizzata.
              </p>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Map section IDs to menu item names for visibility check
  const sectionToMenuMap: { [key: string]: string } = {
    'leads': 'leads',
    'clients': 'clients', 
    'offers': 'quotes',
    'projects': 'projects',
    'calendar': 'calendar',
    'inventory': 'inventory'
  };

  const filteredSections = sections.filter(section => {
    // Always show overview and settings
    if (section.id === 'overview' || section.id === 'settings') {
      return true;
    }
    
    // Check if the corresponding menu item is visible
    const menuItem = sectionToMenuMap[section.id];
    if (menuItem && !isMenuItemVisible(menuItem)) {
      return false;
    }
    
    // Filter by search query
    return section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           section.searchTerms.some(term => 
             term.toLowerCase().includes(searchQuery.toLowerCase())
           );
  });

  // Function to get relevant guide context based on user query
  const getRelevantGuideContext = (query: string): string => {
    const queryLower = query.toLowerCase();
    const relevantSections: GuideSection[] = [];

    // Find sections matching query keywords
    sections.forEach(section => {
      const matches = section.searchTerms.some(term =>
        queryLower.includes(term.toLowerCase())
      ) || section.title.toLowerCase().includes(queryLower);

      if (matches) {
        relevantSections.push(section);
      }
    });

    // If no specific match, return general context
    if (relevantSections.length === 0) {
      return `
FUNZIONALITÀ PRINCIPALI DISPONIBILI:
- Gestione Clienti e Lead (conversione, stati, pipeline)
- Offerte e Preventivi (template, PDF, variabili)
- Progetti (task, milestone, Gantt, tracking)
- WhatsApp (chat, automazioni, template)
- Newsletter (campagne email, segmentazione)
- Workflow Automation (trigger, azioni automatiche)
- Fatturazione (Fatture in Cloud, Invoice Queue)
- Magazzino (inventario, sync WooCommerce)
- Calendar (eventi, sincronizzazione)
- Commissioni (calcolo automatico vendite)
- Partner (collaboratori esterni, task)
- Integrazioni (Google, Fatture in Cloud, API)

Per info specifiche su una funzione, chiedi dettagli (es: "come funziona WhatsApp?" o "come creare un'offerta?")
      `.trim();
    }

    // Build context from relevant sections (limit to 2-3 most relevant)
    let context = '\n📚 INFORMAZIONI RILEVANTI:\n\n';
    relevantSections.slice(0, 3).forEach(section => {
      context += `\n🔹 ${section.title.toUpperCase()}:\n`;

      // Extract text content from React nodes (simplified extraction)
      const sectionId = section.id;

      // Specific context for each section
      switch(sectionId) {
        case 'whatsapp':
          context += `- ACCESSO: Menu principale → WhatsApp | Dalla scheda Cliente/Lead
- SETUP: Menu WhatsApp → Scansiona QR Code con WhatsApp Business → Connessione automatica
- CHAT: Inbox unificata, associazione automatica a clienti/lead, media support
- AUTOMAZIONI: Risposte automatiche intelligenti con comandi: "lista lead", "i miei clienti", "statistiche", "help"
- TEMPLATE: Crea template messaggi con variabili {{client_name}}, {{company_name}}
- BEST PRACTICES: Rispondi entro 24h, usa template per messaggi ricorrenti, mantieni conversazioni associate
`;
          break;

        case 'newsletter':
          context += `- ACCESSO: Menu principale → Newsletter
- CREAZIONE: Nome campagna, oggetto, segmentazione destinatari (tutti/categoria/tag)
- EDITOR: Formattazione ricca, variabili dinamiche {{nome}}, {{azienda}}, template riutilizzabili
- INVIO: Immediato, programmato, o test
- ANALYTICS: Tasso apertura, click rate, bounce
- GDPR: Assicurati consenso opt-in, includi link unsubscribe
`;
          break;

        case 'workflow':
          context += `- ACCESSO: Impostazioni → Workflow Automation
- STRUTTURA: Trigger (quando) → Condizioni (se) → Azioni (allora)
- TRIGGER: Lead creato, offerta accettata, progetto completato, schedulati (giornalieri/settimanali)
- AZIONI: Invia email/WhatsApp, crea task, aggiungi tag, webhook HTTP
- ESEMPI: Follow-up automatico lead, onboarding cliente, rinnovo servizi
- LIMITI: Max 50 workflow attivi, 100 email/ora per workflow
`;
          break;

        case 'invoicing':
          context += `- ACCESSO: Menu → Invoice Queue / Invoice Matching
- SETUP Fatture in Cloud: Impostazioni → Integrazioni → Fatture in Cloud (Company ID, API Key, Secret)
- INVOICE QUEUE: Generazione automatica da offerte/progetti, schedulazione, controllo duplicati
- STATI: Bozza, In Coda, Inviata, Errore
- MATCHING: Abbinamento automatico fatture FiC ↔ Clienti/Offerte CRM
- CONFORMITÀ: Conservazione 10 anni, marca da bollo €77,47+, termine emissione 12gg
`;
          break;

        case 'commissions':
          context += `- ACCESSO: Menu → Commissioni | Impostazioni → Commissioni
- CONFIGURAZIONE: Percentuali globali (Standard, Manager, Senior, Junior) o per utente
- CALCOLO BASE: Deal vinti, progetti completati, MRR ricorrente
- SCAGLIONI: Progressivi basati su fatturato (es: 0-50k→3%, 50-100k→5%, 100k+→7%)
- DASHBOARD: Commissioni mese/anno, deal pagati, % media
- APPROVAZIONE: Calcolo automatico ma richiede approvazione manuale per pagamento
`;
          break;

        case 'partners':
          context += `- ACCESSO: Menu → Partner | Partner Tasks
- TIPOLOGIE: Creativi (designer, copywriter), Tecnici (dev, sistemisti), Consulenti (legali, HR)
- CREAZIONE: Partner → Nuovo (nome, email, P.IVA, competenze, tariffa)
- TASK: Assegnazione con brief, scadenza, budget, tracking ore
- STATI TASK: Da Assegnare, Assegnata, In Corso, In Revisione, Completata
- PAGAMENTI: Tracking costi, timesheet, milestone, ritenute fiscali
`;
          break;

        case 'integrations':
          context += `- ACCESSO: Impostazioni → API e Integrazioni
- Fatture in Cloud: Setup API Key/Secret, sync clienti, invoice queue
- Google Services: Calendar sync, Drive backup, Gmail, Meet
- WhatsApp WAHA: URL endpoint, API Key, QR code scan
- WooCommerce: Sync prodotti bidirezionale, ordini→stock automatico
- API REST: Endpoints /api/clients, /api/leads, auth Bearer token
- SICUREZZA: Non condividere API key, ruota ogni 3-6 mesi, usa HTTPS
`;
          break;

        case 'settings':
          context += `- DATI AZIENDA: Impostazioni > Dati Azienda (nome, indirizzo, P.IVA, logo)
- UTENTI: Impostazioni > Utenti (ruoli Admin/Manager/User)
- CATEGORIE: Impostazioni > Categorie (PREREQUISITO per servizi!)
- SERVIZI: Impostazioni > Servizi (template variabili {{client_name}}, {{service_amount}})
- METODI PAGAMENTO: Impostazioni > Metodi di Pagamento
- COMMISSIONI: Impostazioni > Commissioni (percentuali personalizzate)
`;
          break;

        case 'leads':
          context += `- CONFIGURAZIONE STATI: Impostazioni > Clienti > Stati e Fonti (NON "Gestione Lead"!)
- CREAZIONE: Menu Lead > Nuovo Lead (nome, email, telefono, fonte, budget, assegnatario)
- STATI PERSONALIZZABILI: Nuovo, Contatto, Qualificato, Cliente, Perso
- GESTIONE: Drag&drop Kanban o dropdown tabella
- CONVERSIONE: Pulsante "Converti in Cliente"
- FILTRI: Per stato, fonte, assegnatario, tag
`;
          break;

        case 'clients':
          context += `- CREAZIONE: Menu Clienti > Nuovo Cliente
- TIPI: Privato, Azienda, Partner
- OBBLIGATORI: Nome, email, tipo cliente
- OPZIONALI: Telefono, indirizzo, P.IVA, referente, note
- CONVERSIONE DA LEAD: Pulsante nella pagina lead
- CONFIGURAZIONE: Impostazioni > Clienti (colonne, tag, tipologie partner, settori)
- WHATSAPP: Chat diretta dalla scheda cliente
`;
          break;

        case 'offers':
          context += `- CREAZIONE: Menu Offerte > Nuova Offerta
- PREREQUISITI: Cliente esistente + Categoria Servizi configurata
- VARIABILI TEMPLATE: {{client_name}}, {{service_amount}}, {{total_amount}}, {{service_title}}
- IA: Pulsante "Genera con IA" per introduzione automatica
- PDF: Generazione automatica con logo aziendale
- STATI: Bozza, Inviata, Accettata, Rifiutata
- AZIONI: Duplica offerta, Converti in Progetto (su accettate)
`;
          break;

        case 'projects':
          context += `- STATI: Pianificazione → In Corso → In Pausa → Completato → Annullato
- TASK: Creazione, assegnazione, scadenze, priorità
- TEMPLATE: Task ricorrenti e automatiche
- MILESTONE: Obiettivi intermedi
- TRACKING: Ore e costi in tempo reale
- REPORT: Progresso visibile con grafici
`;
          break;

        case 'calendar':
          context += `- VISTE: Giorno, Settimana, Mese, Lista
- TIPOLOGIE: Appuntamenti, Task, Scadenze, Promemoria
- SINCRONIZZAZIONE: Automatica con task progetti, scadenze offerte, rinnovi
- INTEGRAZIONE: Calendar esterni (Google Calendar)
- FILTRI: Per tipo evento, cliente/progetto, responsabile, priorità
`;
          break;

        case 'inventory':
          context += `- GESTIONE: Prodotti con SKU univoci, giacenze, stock min/max
- MOVIMENTI: Carico/scarico, scadenze, lotti
- CATEGORIE: Organizzazione gerarchica
- LISTINI: Multipli (Standard, Grossisti, Noleggio), scaglioni quantità
- ZONE: Stoccaggio fisico organizzato
- WOOCOMMERCE: Sync bidirezionale (Magazzino > Sync eCommerce)
  * Setup: Impostazioni > API > WooCommerce (URL, Consumer Key/Secret)
  * Export: CRM → WooCommerce (categorie, prodotti SKU)
  * Import: WooCommerce → CRM (prodotti, immagini, prezzi)
`;
          break;

        default:
          context += '- Consulta la sezione specifica nella guida per dettagli completi\n';
      }
    });

    return context;
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || isLoadingChat) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: chatInput,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsLoadingChat(true);

    try {
      // Get relevant context based on user query
      const relevantContext = getRelevantGuideContext(chatInput);

      const response = await generateTextWithChatGpt(
        `Sei l'assistente IA specializzato per questo CRM. Aiuti gli utenti con navigazione, configurazione, troubleshooting e best practices.

${relevantContext}

⚠️ REGOLE FONDAMENTALI:
1. USA SOLO percorsi di menu che esistono realmente (specificati sopra)
2. Se non sei sicuro di una funzione, dillo chiaramente invece di inventare
3. Fornisci percorsi completi di navigazione: Menu → Sezione → Azione
4. Spiega prerequisiti quando necessari (es: categorie devono esistere prima di creare servizi)
5. Usa formattazione HTML (liste <ul><li>, grassetto <strong>, paragrafi <p>) per chiarezza
6. Rispondi in italiano con tono professionale ma amichevole
7. Se l'utente chiede qualcosa che non esiste, suggerisci alternative reali disponibili

ERRORI DA EVITARE:
- NON inventare percorsi di menu che non esistono
- NON dire "Vai in Impostazioni > Gestione Lead" (percorso corretto: "Impostazioni > Clienti > Stati e Fonti")
- NON suggerire funzioni non disponibili nel CRM

Domanda utente: "${chatInput}"

Fornisci una risposta chiara, precisa e ben formattata in HTML.`
      );

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.text || response.error || 'Scusa, non sono riuscito a processare la tua richiesta.',
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Scusa, si è verificato un errore. Riprova più tardi.',
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoadingChat(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex">
        {/* Sidebar */}
        <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Book className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Guida CRM</h2>
                  <p className="text-sm text-gray-600">Assistenza completa</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca nella guida..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {filteredSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => {
                    setActiveSection(section.id);
                    setShowChat(false); // Chiudi la chat e mostra la sezione
                  }}
                  className={`w-full text-left p-3 rounded-lg flex items-center space-x-3 transition-colors ${
                    activeSection === section.id && !showChat
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {section.icon}
                  <span className="font-medium">{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Chat Toggle */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => setShowChat(!showChat)}
              className={`w-full p-3 rounded-lg flex items-center justify-center space-x-2 transition-colors ${
                showChat
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              <MessageCircle className="h-5 w-5" />
              <span className="font-medium">
                {showChat ? 'Chiudi Chat IA' : 'Assistente IA'}
              </span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {showChat ? (
            // Chat Interface
            <div className="flex-1 flex flex-col">
              <div className="p-6 border-b border-gray-200 bg-green-50">
                <h3 className="text-lg font-semibold text-green-800 flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Assistente IA - Chiedi aiuto
                </h3>
                <p className="text-sm text-green-600 mt-1">
                  Fai una domanda specifica sul CRM e riceverai aiuto personalizzato
                </p>
              </div>

              {/* Chat Messages */}
              <div 
                ref={chatMessagesRef}
                className="flex-1 overflow-y-auto p-6 space-y-4" 
                style={{ maxHeight: 'calc(90vh - 200px)' }}
              >
                {chatMessages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-12">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">Benvenuto nell'Assistente IA</p>
                    <p className="text-sm mt-2">
                      Fai una domanda su come utilizzare il CRM e riceverai aiuto immediato
                    </p>
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                      <button
                        onClick={() => setChatInput('Come creo la mia prima offerta?')}
                        className="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        "Come creo la mia prima offerta?"
                      </button>
                      <button
                        onClick={() => setChatInput('Come configuro le impostazioni base?')}
                        className="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        "Come configuro le impostazioni base?"
                      </button>
                      <button
                        onClick={() => setChatInput('Come gestisco i lead in modo efficace?')}
                        className="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        "Come gestisco i lead in modo efficace?"
                      </button>
                      <button
                        onClick={() => setChatInput('Come organizzo i progetti?')}
                        className="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 text-sm"
                      >
                        "Come organizzo i progetti?"
                      </button>
                    </div>
                  </div>
                ) : (
                  chatMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-2xl p-4 rounded-2xl ${
                          message.type === 'user'
                            ? 'bg-[#0f6cbd] text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {message.type === 'assistant' ? (
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: message.content }}
                          />
                        ) : (
                          <p className="whitespace-pre-wrap">{message.content}</p>
                        )}
                        <p className={`text-xs mt-2 ${
                          message.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {message.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                {isLoadingChat && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 p-4 rounded-2xl flex items-center space-x-2">
                      <Loader className="h-4 w-4 animate-spin text-gray-600" />
                      <span className="text-gray-600">L'assistente sta pensando...</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div className="p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Scrivi la tua domanda..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoadingChat}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!chatInput.trim() || isLoadingChat}
                    className="px-6 py-3 bg-[#0f6cbd] text-white rounded-lg hover:bg-[#0a5ba8] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="h-4 w-4" />
                    <span>Invia</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            // Guide Content
            <div className="flex-1 overflow-y-auto">
              <div className="p-8">
                {filteredSections.find(s => s.id === activeSection)?.content}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}